import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { X, CheckCircle, AlertCircle, Info, XCircle } from 'lucide-react';

export type ToastType = 'success' | 'error' | 'info' | 'warning';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastProps {
  toast: Toast;
  onClose: (id: string) => void;
}

const slideIn = keyframes`
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;


const ToastContainer = styled.div<{ type: ToastType }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} ${theme.spacing.lg};
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-left: 4px solid ${({ type }) => {
    switch (type) {
      case 'success': return theme.colors.success;
      case 'error': return theme.colors.danger;
      case 'warning': return theme.colors.warning;
      default: return theme.colors.info;
    }
  }};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.lg};
  min-width: 300px;
  max-width: 500px;
  animation: ${slideIn} 0.3s ease-out;
  font-family: 'Roboto', sans-serif;
  position: relative;
  z-index: 10000;
`;

const IconContainer = styled.div<{ type: ToastType }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ type }) => {
    switch (type) {
      case 'success': return theme.colors.success;
      case 'error': return theme.colors.danger;
      case 'warning': return theme.colors.warning;
      default: return theme.colors.info;
    }
  }};
`;

const Message = styled.div`
  flex: 1;
  color: ${theme.colors.text};
  font-size: ${theme.fontSize.base};
  line-height: 1.5;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${theme.spacing.xs};
  color: ${theme.colors.textMuted};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
  flex-shrink: 0;
  &:hover {
    color: ${theme.colors.text};
  }
  &:focus-visible {
    outline: 2px solid ${theme.colors.primary};
    outline-offset: 2px;
    border-radius: ${theme.borderRadius.sm};
  }
`;

const getIcon = (type: ToastType) => {
  switch (type) {
    case 'success':
      return <CheckCircle size={20} />;
    case 'error':
      return <XCircle size={20} />;
    case 'warning':
      return <AlertCircle size={20} />;
    default:
      return <Info size={20} />;
  }
};

export const ToastItem: React.FC<ToastProps> = ({ toast, onClose }) => {
  useEffect(() => {
    const duration = toast.duration ?? 5000;
    const timer = setTimeout(() => {
      onClose(toast.id);
    }, duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onClose]);

  return (
    <ToastContainer type={toast.type}>
      <IconContainer type={toast.type}>
        {getIcon(toast.type)}
      </IconContainer>
      <Message>{toast.message}</Message>
      <CloseButton
        onClick={() => onClose(toast.id)}
        aria-label="Fechar notificação"
      >
        <X size={18} />
      </CloseButton>
    </ToastContainer>
  );
};

const ToastWrapper = styled.div`
  position: fixed;
  top: ${theme.spacing.xl};
  right: ${theme.spacing.xl};
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.sm};
  pointer-events: none;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    top: ${theme.spacing.md};
    right: ${theme.spacing.md};
    left: ${theme.spacing.md};
  }
  
  > * {
    pointer-events: auto;
  }
`;

interface ToastProviderProps {
  children: React.ReactNode;
  toasts: Toast[];
  onClose: (id: string) => void;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children, toasts, onClose }) => {
  return (
    <>
      {children}
      {toasts.length > 0 && (
        <ToastWrapper>
          {toasts.map(toast => (
            <ToastItem key={toast.id} toast={toast} onClose={onClose} />
          ))}
        </ToastWrapper>
      )}
    </>
  );
};
